/**
 * Imports
 */
import {
  FirebaseCollectionsType,
  deleteAtPath,
  deleteFolder,
  listFolder,
  resolveDownloadUrl,
  uploadToPath,
} from 'firebaseAPI';
import moment from 'moment';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { UploadDocument, getFileExtension } from 'utils';
import { merchantSelector } from '../redux';
/**
 * Types
 */
export type DocumentsApiAPIHandlers = {
  onDeleteDocument: (fullPath: string) => Promise<void>;
  onDeleteAllDocuments: (path: string) => Promise<void>;
  onDownloadImage: (fullPath: string) => Promise<void>;
  onGetDocuments: () => Promise<DocumentObj[]>;
  onUpdateCompanyDocuments: (document: UploadDocument) => Promise<void>;
};
export type DocumentsApiAPIType = {
  handlers: DocumentsApiAPIHandlers;
  isLoading: boolean;
  documents: DocumentObj[];
};
export interface DocumentsApiProps {
  userId: string;
  merchantId: string;
  accoutId: string;
}
/**
 * CONST
 */
const DOCUMENT_ROOT_FOLDER = 'onboarding_documents';
export type DocumentObj = {
  name: string;
  /** Path inside the bucket — what downloads and deletes address. */
  fullPath: string;
  url: string;
};

/**
 * useDocumentsApi
 */
const useDocumentsApi = ({
  userId,
  merchantId,
  accoutId,
}: DocumentsApiProps): DocumentsApiAPIType => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [documents, setDocuments] = React.useState<DocumentObj[]>([]);
  const merchant = useSelector(merchantSelector(merchantId));
  const firestore = useFirestore();

  /**
   * The folder holding this merchant's documents.
   *
   * An existing `companyDocumentsPath` is authoritative — onboarding records it, and
   * the new support dashboard reads and writes it — and only when there is none do we
   * derive where new uploads should go, preferring the account id like that dashboard
   * does. Deliberately no bucket in this decision: which bucket the files sit in is
   * *not* something the path can tell us. The previous `acct_`-in-the-path guess
   * assumed it could, and so missed every merchant whose new customer-files uploads
   * went to its old, pre-`acct_` path — which is exactly what onboarding recorded.
   */
  const rootFolderPath = React.useMemo(() => {
    if (merchant?.companyDocumentsPath) {
      return merchant.companyDocumentsPath;
    }
    return `${DOCUMENT_ROOT_FOLDER}/${accoutId || userId}`;
  }, [merchant?.companyDocumentsPath, accoutId, userId]);

  /**
   * Download file
   * @param - fullPath a path on firebase storage, in either bucket
   */
  const _downloadFileFromPath = async (fullPath: string): Promise<any> => {
    resolveDownloadUrl(fullPath)
      .then((url) => {
        const fileName = fullPath.split('/').pop() || fullPath;

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const blob = xhr.response;
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        };
        xhr.open('GET', url);
        xhr.send();
      })
      // Without this the click just does nothing, which is what made the original bug
      // look like a broken button rather than "we looked in the wrong bucket".
      .catch((error) => {
        console.error(
          '[useDocumentsApi] document unavailable in every bucket',
          fullPath,
          error,
        );
        alert(`Could not download ${fullPath}`);
      });
  };

  /**
   * Get path for documents
   */
  const _getStoragePathForDocument = (
    documentName: string,
    fileExtension: string,
  ) => {
    const timestamp = moment().format('DD.MM.YY_HH.mm');
    return `${rootFolderPath}/${documentName}_${timestamp}.${fileExtension}`;
  };

  /**
   * Upload document — always to customer-files, whichever bucket the merchant's
   * existing documents happen to be in.
   */
  const _uploadDocument = async (file: File, documentName: string) => {
    const fileExtension = getFileExtension(file.type);
    const storagePath = _getStoragePathForDocument(documentName, fileExtension);
    const metadata = {
      cacheControl: 'public, max-age=900',
      contentType: file.type,
    };
    await uploadToPath(storagePath, file, metadata);
    return storagePath;
  };

  const handlers: DocumentsApiAPIHandlers = {
    /**
     * onDeleteDocument
     */
    onDeleteDocument: async (fullPath: string) => {
      setIsLoading(true);
      try {
        await deleteAtPath(fullPath);
      } finally {
        setIsLoading(false);
      }
    },
    /**
     * onDeleteAllDocuments
     */
    onDeleteAllDocuments: async (path: string) => {
      // Both the folder the caller names and the one this merchant actually uses: a
      // merchant whose `companyDocumentsPath` points at the account id still has to be
      // purged when the caller only knows the onboarding id, and vice versa.
      const folders = [
        `${DOCUMENT_ROOT_FOLDER}/${path}`,
        rootFolderPath,
      ].filter((folder, index, all) => all.indexOf(folder) === index);
      const results = await Promise.all(folders.map(deleteFolder));
      const failed = results.reduce((sum, r) => sum + r.failed, 0);
      const unreadable = results.reduce(
        (sum, r) => sum + r.unreadableBuckets,
        0,
      );
      if (failed > 0 || unreadable > 0) {
        // Surfaced, not swallowed: personal data still sitting in a bucket after a GDPR
        // purge is the one outcome nobody should have to discover later.
        alert(
          `Document purge incomplete: ${failed} file(s) could not be deleted and ${unreadable} bucket(s) could not be listed — check the console and remove them manually.`,
        );
      }
    },
    /**
     * onGetDocuments
     */
    onGetDocuments: async () => {
      setIsLoading(true);
      try {
        // Merged across buckets: onboarding-era files are in the project default
        // bucket and anything the new dashboard added is in customer-files, under the
        // same folder.
        const documents = await listFolder(rootFolderPath);
        setDocuments(documents);
        setIsLoading(false);
        return documents;
      } catch (error) {
        alert(JSON.stringify(error.message));
        setIsLoading(false);
        return [];
      }
    },
    /**
     * onUpdateCompanyDocuments
     */
    onUpdateCompanyDocuments: async (data) => {
      await _uploadDocument(data.file, data.name);
      if (!merchant?.companyDocumentsPath) {
        await firestore
          .collection(FirebaseCollectionsType.MERCHANTS)
          .doc(merchantId)
          .update({
            companyDocumentsPath: rootFolderPath,
          });
      }
      await handlers.onGetDocuments();
    },
    /**
     * onDownloadImage
     */
    onDownloadImage: async (fullPath) => {
      await _downloadFileFromPath(fullPath);
    },
  };

  return { handlers, isLoading, documents };
};

/**
 * Exports
 */
export { useDocumentsApi };
